// ─── Tile constants ────────────────────────────────────────────────────────────
export const TILE = {
  GRASS: 0,
  PATH:  1,
  ROAD:  2,
  WALL:  3,
  TREE:  4,
  DOOR:  5,
  DECOR: 6,  // non-walkable, draws as grass — used for decoration footprints
};

export const TILE_SIZE = 32;
export const MAP_W = 52;
export const MAP_H = 48;
export const PLAYER_START = { x: 22, y: 24 }; // just south of the fountain

// ─── Zone configurations ───────────────────────────────────────────────────────
export const ZONES = {
  gp: {
    id: "gp",
    name: "GP Clinic",
    emoji: "🩺",
    description: "Outpatient consultations — UTIs, sore throats, STIs, tick bites, and more.",
    wallColor: "#3b82f6",
    enemies: ["ecoli-uti", "s-saprophyticus", "strep-pyogenes", "chlamydia", "neisseria-gonorrhoeae", "h-pylori", "hsv", "borrelia", "treponema", "mssa"],
  },
  hotel: {
    id: "hotel",
    name: "Grand Hotel",
    emoji: "🏨",
    description: "The hotel restaurant chef has been unwell... multiple guests are presenting with acute gastroenteritis.",
    wallColor: "#f59e0b",
    enemies: ["salmonella-nts", "influenza-a", "sars-cov2"],
  },
  hospital: {
    id: "hospital",
    name: "City Hospital",
    emoji: "🏥",
    description: "The ICU and infectious disease ward. Only the hardest cases end up here.",
    wallColor: "#22c55e",
    enemies: [
      "tb", "candida", "plasmodium", "pseudomonas", "aspergillus", "hiv",
      "cryptococcus", "mucor", "vre", "toxoplasma", "pcp", "acinetobacter",
      "cmv", "esbl-ecoli", "strongyloides", "cre", "n-meningitidis", "mrsa", "tetanus",
    ],
  },
  pharmacy: {
    id: "pharmacy",
    name: "Pharmacy",
    emoji: "💊",
    description: "Check your coins and XP. More items coming soon!",
    wallColor: "#a855f7",
    enemies: [],
    isShop: true,
  },
  mall: {
    id: "mall",
    name: "Shopping Mall",
    emoji: "🛍️",
    description: "A busy public space. Community-acquired infections spread easily here.",
    wallColor: "#f97316",
    enemies: ["strep-pneumo", "influenza-a", "sars-cov2", "mrsa"],
  },
  school: {
    id: "school",
    name: "Westside School",
    emoji: "🏫",
    description: "Children and teenagers in close quarters — perfect for respiratory bugs and gastroenteritis.",
    wallColor: "#eab308",
    enemies: ["strep-pyogenes", "bordetella", "h-influenzae", "salmonella-nts"],
  },
  kindergarten: {
    id: "kindergarten",
    name: "Sunshine Kindergarten",
    emoji: "🌈",
    description: "Young children with developing immune systems. Chickenpox, whooping cough, and more.",
    wallColor: "#ec4899",
    enemies: ["vzv", "bordetella", "h-influenzae", "gbs"],
  },
  oldpeoplesHome: {
    id: "oldpeoplesHome",
    name: "Rosewood Care Home",
    emoji: "🏡",
    description: "Elderly residents with multiple comorbidities. Watch for atypical presentations.",
    wallColor: "#6b7280",
    enemies: ["strep-pneumo", "influenza-a", "sars-cov2", "mrsa", "cdiff", "pseudomonas", "vre"],
  },
};

// ─── Map builder ───────────────────────────────────────────────────────────────
function buildMap() {
  const map = Array.from({ length: MAP_H }, () => Array(MAP_W).fill(TILE.GRASS));
  const wallColors = Array.from({ length: MAP_H }, () => Array(MAP_W).fill(null));

  const fill = (x, y, w, h, tile, color = null) => {
    for (let r = y; r < y + h && r < MAP_H; r++) {
      for (let c = x; c < x + w && c < MAP_W; c++) {
        if (c >= 0 && r >= 0) {
          map[r][c] = tile;
          if (color !== null) wallColors[r][c] = color;
        }
      }
    }
  };

  const setDoor = (col, row) => {
    map[row][col] = TILE.DOOR;
    wallColors[row][col] = null;
  };

  // Roads
  fill(0,  13, MAP_W, 3, TILE.ROAD); // H-road 1 (rows 13-15)
  fill(0,  29, MAP_W, 3, TILE.ROAD); // H-road 2 (rows 29-31)
  fill(0,  44, MAP_W, 3, TILE.ROAD); // H-road 3 (rows 44-46) — south of bottom buildings
  fill(12, 0,  3, MAP_H, TILE.ROAD); // V-road 1 (cols 12-14)
  fill(32, 0,  3, MAP_H, TILE.ROAD); // V-road 2 (cols 32-34)

  // GP Clinic: cols 1-10, rows 1-11  →  south door at [col5, row11]
  fill(1,  1, 10, 11, TILE.WALL, ZONES.gp.wallColor);
  setDoor(5, 11);

  // Hotel: cols 16-30, rows 1-11  →  south door at [col23, row11]
  fill(16, 1, 15, 11, TILE.WALL, ZONES.hotel.wallColor);
  setDoor(23, 11);

  // Hospital: cols 36-50, rows 1-11  →  south door at [col43, row11]
  fill(36, 1, 15, 11, TILE.WALL, ZONES.hospital.wallColor);
  setDoor(43, 11);

  // Pharmacy: cols 1-10, rows 17-27  →  south door at [col5, row27]
  fill(1,  17, 10, 11, TILE.WALL, ZONES.pharmacy.wallColor);
  setDoor(5, 27);

  // Mall: cols 36-50, rows 17-27  →  north door at [col43, row17]
  fill(36, 17, 15, 11, TILE.WALL, ZONES.mall.wallColor);
  setDoor(43, 17);

  // School: cols 1-10, rows 32-43  →  south door at [col5, row43]
  fill(1,  32, 10, 12, TILE.WALL, ZONES.school.wallColor);
  setDoor(5, 43);

  // Kindergarten: cols 16-30, rows 32-43  →  south door at [col23, row43]
  fill(16, 32, 15, 12, TILE.WALL, ZONES.kindergarten.wallColor);
  setDoor(23, 43);

  // Old People's Home: cols 36-50, rows 32-43  →  south door at [col43, row43]
  fill(36, 32, 15, 12, TILE.WALL, ZONES.oldpeoplesHome.wallColor);
  setDoor(43, 43);

  // Path tiles in front of building entrances
  [
    [5,  12], [23, 12], [43, 12],  // south of top buildings
    [5,  16], [43, 16],            // north of middle buildings
    [5,  28], [43, 28],            // south of middle buildings
    [5,  44], [23, 44], [43, 44],  // south of bottom buildings (between door and road)
  ].forEach(([c, r]) => {
    if (map[r][c] === TILE.GRASS) map[r][c] = TILE.PATH;
  });

  // Border trees
  for (let c = 0; c < MAP_W; c++) {
    if (map[0][c]        === TILE.GRASS) map[0][c]        = TILE.TREE;
    if (map[MAP_H-1][c]  === TILE.GRASS) map[MAP_H-1][c]  = TILE.TREE;
  }
  for (let r = 0; r < MAP_H; r++) {
    if (map[r][0]        === TILE.GRASS) map[r][0]        = TILE.TREE;
    if (map[r][MAP_W-1]  === TILE.GRASS) map[r][MAP_W-1]  = TILE.TREE;
  }

  // Fountain footprint — non-walkable decoration tiles (cols 20-23, rows 19-21)
  fill(20, 19, 4, 3, TILE.DECOR);

  // Park trees (center open area: cols 15-31, rows 16-28)
  [
    [15,16],[31,16],[15,28],[31,28],
    [17,18],[29,19],[18,25],[27,23],
    [21,17],[25,27],[17,21],[29,26],
  ].forEach(([c, r]) => {
    if (map[r][c] === TILE.GRASS) map[r][c] = TILE.TREE;
  });

  // Decorative trees between buildings and roads
  [
    [11,2],[11,6],[11,10],          // east of GP
    [35,2],[35,6],[35,10],          // west of Hospital
    [11,18],[11,22],[11,26],        // east of Pharmacy
    [35,18],[35,22],[35,26],        // west of Mall
    [11,33],[11,38],[11,42],        // east of School
    [35,33],[35,38],[35,42],        // west of OPH
    [15,42],[31,42],                // flanking Kindergarten south entrance
  ].forEach(([c, r]) => {
    if (r >= 0 && r < MAP_H && c >= 0 && c < MAP_W && map[r][c] === TILE.GRASS)
      map[r][c] = TILE.TREE;
  });

  return { map, wallColors };
}

const { map: _map, wallColors: _wallColors } = buildMap();
export const MAP = _map;
export const WALL_COLORS = _wallColors;

// ─── Buildings ─────────────────────────────────────────────────────────────────
// Add an `image` path to any building to replace the programmatic drawing with
// a sprite. Leave `image` absent (or null) to keep the generated appearance.
export const BUILDINGS = [
  { zoneId: "gp",           name: "GP Clinic",             emoji: "🩺", x: 1,  y: 1,  w: 10, h: 11, door: { x: 5,  y: 11 }, image: "/environment/gp.png", imageScale: 0.75, imageAlign: "bottom" },
  { zoneId: "hotel",        name: "Grand Hotel",           emoji: "🏨", x: 16, y: 1,  w: 15, h: 11, door: { x: 23, y: 11 }, image: "/environment/hotel.png" },
  { zoneId: "hospital",     name: "City Hospital",         emoji: "🏥", x: 36, y: 1,  w: 15, h: 11, door: { x: 43, y: 11 }, image: "/environment/hospital1.png" },
  { zoneId: "pharmacy",     name: "Pharmacy",              emoji: "💊", x: 1,  y: 17, w: 10, h: 11, door: { x: 5,  y: 27 }, image: "/environment/pharmacy.png", imageScale: 0.55, imageAlign: "bottom" },
  { zoneId: "mall",         name: "Shopping Mall",         emoji: "🛍️", x: 36, y: 17, w: 15, h: 11, door: { x: 43, y: 17 }, image: "/environment/shoppingmall.png" },
  { zoneId: "school",       name: "Westside School",       emoji: "🏫", x: 1,  y: 32, w: 10, h: 12, door: { x: 5,  y: 43 }, image: "/environment/elementaryschool.png" },
  { zoneId: "kindergarten", name: "Sunshine Kindergarten", emoji: "🌈", x: 16, y: 32, w: 15, h: 12, door: { x: 23, y: 43 }, image: "/environment/kindergarden.png", imageScale: 0.75, imageAlign: "bottom" },
  { zoneId: "oldpeoplesHome", name: "Rosewood Care Home",  emoji: "🏡", x: 36, y: 32, w: 15, h: 12, door: { x: 43, y: 43 }, image: "/environment/nursinghome.png", imageScale: 0.75, imageAlign: "bottom" },
];

// ─── Decorations ───────────────────────────────────────────────────────────────
// Decorative objects drawn over tiles. tx/ty = top-left tile, tw/th = size in tiles.
export const DECORATIONS = [
  { image: "/environment/fountain.png", tx: 20, ty: 19, tw: 4, th: 3 },
];

// ─── NPCs ──────────────────────────────────────────────────────────────────────
export const NPCS = [
  {
    id: "npc-martinez", x: 26, y: 20, color: "#3b82f6", name: "Dr. Martinez",
    sprite: "/blobavatar/blob.doctor.blue.png",
    lines: [
      "Did you know the smallpox vaccine was the first vaccine ever created? Edward Jenner developed it in 1796!",
      "He noticed milkmaids who caught cowpox seemed immune to smallpox. The word 'vaccine' comes from 'vacca' — Latin for cow!",
    ],
  },
  {
    id: "npc-chen", x: 19, y: 24, color: "#10b981", name: "Nurse Chen",
    sprite: "/blobavatar/blob.doctor.blue.png",
    lines: [
      "The golden age of antibiotics was 1940–1970. Almost every class we use today was discovered in those 30 years.",
      "Since then, very few new antibiotic classes have been found. That's why antimicrobial stewardship is so critical!",
    ],
  },
  {
    id: "npc-williams", x: 27, y: 21, color: "#8b5cf6", name: "Prof. Williams",
    sprite: "/blobavatar/blob.scientist.png",
    lines: [
      "Bacteria can share resistance genes between each other via horizontal gene transfer — even across different species!",
      "One resistant bacterium can share its resistance plasmid with thousands of neighbours within hours. Nature is terrifying!",
    ],
  },
  {
    id: "npc-lee", x: 6, y: 28, color: "#f59e0b", name: "Pharmacist Lee",
    sprite: "/blobavatar/blob.moneyman.png",
    lines: [
      "Antibiotic resistance is projected to cause 10 million deaths per year by 2050 if we don't act now.",
      "Always complete your antibiotic course — stopping early lets resistant bacteria survive and multiply!",
    ],
  },
  {
    id: "npc-garcia", x: 43, y: 16, color: "#ef4444", name: "Health Inspector Garcia",
    sprite: "/blobavatar/blob.moneyman.png",
    lines: [
      "Community-acquired pneumonia is one of the leading causes of death from infectious disease worldwide.",
      "Simple handwashing reduces transmission of respiratory pathogens by up to 50%! The cheapest intervention we have.",
    ],
  },
  {
    id: "npc-patel", x: 6, y: 12, color: "#06b6d4", name: "Receptionist Patel",
    sprite: "/blobavatar/blob.doctor.blue.png",
    lines: [
      "The average GP consultation is only 9 minutes. A thorough patient history accounts for over 80% of a correct diagnosis!",
    ],
  },
  {
    id: "npc-murphy", x: 24, y: 12, color: "#f97316", name: "Tourist Murphy",
    sprite: "/blobavatar/blob.beerman.png",
    lines: [
      "Food poisoning affects 600 million people worldwide every year — that's 1 in 10 people on the planet!",
      "Norovirus alone causes around 685 million cases of gastroenteritis annually. Always wash your hands before cooking!",
    ],
  },
  {
    id: "npc-okonkwo", x: 22, y: 27, color: "#ec4899", name: "Medical Student Okonkwo",
    sprite: "/blobavatar/blob.scientist.png",
    lines: [
      "Malaria kills a child every 2 minutes in Sub-Saharan Africa. Yet artemisinin-based therapy achieves 95% cure rates.",
      "Distribution and access remain the biggest challenges. Many children die before ever reaching a clinic.",
    ],
  },
  {
    id: "npc-thompson", x: 43, y: 28, color: "#64748b", name: "Carer Thompson",
    sprite: "/blobavatar/blob.doctor.blue.png",
    lines: [
      "People over 65 account for around 50% of antibiotic prescriptions despite being only 15% of the population.",
      "UTIs in the elderly often present atypically — confusion and falls rather than the classic burning and frequency!",
    ],
  },
];
